import React from 'react';
import { CachedImageProps } from '@usepepper/ui/atoms/cached-image';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';
import { OrderableItemCardV2 } from './orderable-item-card-v2';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { VariantPackInfoContext_GroupBadgeFieldsFragment } from '@/types';

interface MinimalOrderableItemCardV2Props {
  actionElement: React.ReactNode;
  badges?: VariantPackInfoContext_GroupBadgeFieldsFragment[];
  description?: string | null;
  imageAlt?: string;
  imageRecyclingKey?: string | null;
  imageUri?: string | null;
  onPress?: () => void;
  promotionBadgeLabel?: string | null;
  renderAsLarge: boolean;
  style?: StyleProp<ViewStyle>;
  subtitle: React.ReactNode;
  title: string;
  width?: number;
}

export const MinimalOrderableItemCardV2: React.FC<
  MinimalOrderableItemCardV2Props
> = ({
  actionElement,
  badges,
  description,
  imageAlt,
  imageRecyclingKey,
  imageUri,
  onPress,
  promotionBadgeLabel,
  renderAsLarge,
  style,
  subtitle,
  title,
  width,
}) => {
  const imageProps: CachedImageProps = {
    accessibilityLabel: imageAlt,
    contentFit: 'cover',
    recyclingKey: imageRecyclingKey ?? imageUri ?? undefined,
    source: {
      uri: imageUri ?? undefined,
    },
    style: [styles.image, styles.imageMaxHeight],
    testID: 'orderable-item-image',
  };

  const cardTitle = (
    <View>
      <Typography numberOfLines={1} variant="paragraph-base-600">
        {title}
      </Typography>
      {renderAsLarge ? (
        description ? (
          <Typography numberOfLines={1} variant="paragraph-md">
            {description}
          </Typography>
        ) : null
      ) : (
        subtitle
      )}
    </View>
  );

  return (
    <OrderableItemCardV2
      badges={badges}
      bottomRightElement={actionElement}
      containerStyle={style}
      disabled={!onPress}
      imageProps={imageProps}
      listDisplaySize={ListDisplaySize.Standard}
      onPress={onPress}
      promoBadgeProps={
        promotionBadgeLabel
          ? {
              label: promotionBadgeLabel,
            }
          : null
      }
      subheader={renderAsLarge ? subtitle : null}
      title={cardTitle}
      width={renderAsLarge && typeof width === 'number' ? width : undefined}
    />
  );
};

const styles = StyleSheet.create({
  image: {
    alignSelf: 'stretch',
    aspectRatio: 1,
    width: '100%',
  },
  imageMaxHeight: {
    maxHeight: 188,
  },
});
